import React from 'react'
import { useTranslation } from 'react-i18next'

import { Button } from '@/components/ui/button'
import { useActionableCommentStore } from '@/features/actionable-comment'
import { useTeamworkSyncStatus } from '@/features/view-tfl-uploaded/hooks/useTeamworkSyncStatus'
import {
  ResponseThread,
  ThreadActionableType,
  ThreadSyncStatus,
} from '@/schema'

import {
  AlertCircle,
  LinkExternal01,
  RefreshCw05,
  X,
} from '../../../components/Icons'

interface TeamworkTaskLinkProps {
  thread: ResponseThread
  assigneeId?: string
}

export const TeamworkTaskLink: React.FC<TeamworkTaskLinkProps> = ({
  thread,
  assigneeId,
}) => {
  const { t } = useTranslation()
  const { setActionableDialogOpen } = useActionableCommentStore()
  const { threadSyncStatus, retryThreadSync, teamworkSyncUrl, actionableType } =
    useTeamworkSyncStatus({
      thread,
    })

  const handleRetrySync = (e: React.MouseEvent) => {
    e.stopPropagation()
    retryThreadSync()
  }

  const handleNoAction = (e: React.MouseEvent) => {
    e.stopPropagation()
    if (thread.thread_id) {
      setActionableDialogOpen({
        open: true,
        threadId: thread.thread_id,
        assigneeId: assigneeId || null,
      })
    }
  }

  // View task: SYNCED + has URL
  if (threadSyncStatus === ThreadSyncStatus.SYNCED && teamworkSyncUrl) {
    return (
      <a
        href={teamworkSyncUrl}
        target="_blank"
        rel="noopener noreferrer"
        className="flex items-center h-auto gap-1 ml-3 text-sm font-medium no-underline text-brand-600 hover:text-brand-700"
        onClick={(e) => e.stopPropagation()}
      >
        <LinkExternal01 size={20} className="text-brand-600" />
        {t('comment.teamworkTask.viewTask')}
      </a>
    )
  }

  // Syncing: SYNCING status
  if (threadSyncStatus === ThreadSyncStatus.SYNCING) {
    return (
      <span className="flex items-center gap-1 ml-3 text-sm font-normal text-foreground-fg-quaternary-400">
        <RefreshCw05
          size={22}
          className="animate-spin text-foreground-fg-quaternary-400"
        />
        {t('comment.teamworkTask.syncing')}
      </span>
    )
  }

  // Sync failed: FAILED status - clickable to retry
  if (threadSyncStatus === ThreadSyncStatus.FAILED) {
    return (
      <Button
        variant="text"
        className="flex items-center h-auto gap-1 px-3 ml-1 text-sm font-medium py- text-error-primary-600 hover:text-error-primary-700"
        onClick={handleRetrySync}
        leadingIcon={
          <AlertCircle size={18} className="text-error-primary-600" />
        }
      >
        {t('comment.teamworkTask.syncFailed')}
      </Button>
    )
  }

  // No action: NOT_ACTIONABLE - clickable to open modal
  if (actionableType === ThreadActionableType.NOT_ACTIONABLE) {
    return (
      <Button
        variant="text"
        className="flex items-center h-auto gap-1 px-2 py-1 ml-1 text-sm font-medium text-warning-primary-600 hover:text-warning-primary-700"
        onClick={handleNoAction}
        leadingIcon={<X size={22} className="text-warning-primary-600" />}
      >
        {t('comment.teamworkTask.noAction')}
      </Button>
    )
  }
  return null
}
